import { Component, inject } from '@angular/core';

import { TranslateModule } from "@ngx-translate/core";
import { RxState, selectSlice } from "@rx-angular/state";
import { GenerischeFreigabeVariables } from "../generische-freigabe.variables";
import { ActivitiContentService } from "@alfresco/adf-core";
import { BaseResultTemplateComponent } from "../../base-result-template.component";
import { AsyncPipe, DatePipe, NgForOf, NgIf, NgTemplateOutlet } from "@angular/common";
import { WorkflowResultProtocol, WorkflowVariables } from "@alf-nx-workspace/shared/interfaces";
import { fromPromise } from "rxjs/internal-compatibility";
import { saveAs } from 'file-saver';

interface ResultTemplateComponentState {
  variableMap: Map<string, string>,
  workflowResult: string,
  workflowResultProtocol: WorkflowResultProtocol[]
}

@Component({
  selector: 'generische-freigabe-result-template',
  template: `
    <ng-container *ngIf="vm$ | async as vm">
      <ng-container *ngIf="vm.workflowResult === workflowResults.successful">
        <a [href]="getContentUrl()"
           (click)="downloadContent($event)">{{ 'LINK_TO_DOCUMENT' | translate }}</a>
      </ng-container>

      <ng-container
        *ngTemplateOutlet="protocolSection; context: { titleKey: 'APPROVED_BY', resultKey: 'WORKFLOW.PROTOCOL.RESULT.FREIGABE' }">
      </ng-container>

      <ng-container
        *ngTemplateOutlet="protocolSection; context: { titleKey: 'REJECTED_BY', resultKey: 'WORKFLOW.PROTOCOL.RESULT.ABLEHNUNG' }">
      </ng-container>

      <ng-template #protocolSection let-titleKey="titleKey" let-resultKey="resultKey">
        <ng-container *ngIf="hasWorkflowProtocolByResult(vm.workflowResultProtocol, resultKey)">
          <div class="margin-top-4">{{ titleKey | translate }}</div>
          <ng-container *ngFor="let entry of filterWorkflowProtocolByResult(vm.workflowResultProtocol, resultKey)">
            <div class="mat-caption">{{ entry.by }} - {{ entry.on }}</div>
          </ng-container>
        </ng-container>
      </ng-template>
    </ng-container>
  `,
  styles: [``],
  standalone: true,
  imports: [ TranslateModule,NgIf, AsyncPipe, NgForOf, DatePipe, NgTemplateOutlet],
  providers: [RxState]
})
export class ResultTemplateComponent extends BaseResultTemplateComponent {
  public readonly fields = GenerischeFreigabeVariables;

  readonly activitiContentService = inject(ActivitiContentService);
  readonly rxState = inject<RxState<ResultTemplateComponentState>>(RxState);
  readonly vm$ = this.rxState.select(selectSlice(["variableMap", "workflowResult", "workflowResultProtocol"]));

  ngOnInit() {
    this.connectState();
  }

  private connectState() {
    this.rxState.connect("workflowResult", this.workflowResultSubject);
    this.rxState.connect("workflowResultProtocol", this.variableMapSubject, (oldState, value) => {
      try {
        return JSON.parse(this.variableMap.get(this.fields._bokuFlow_workflowResultProtocolJSON));
      } catch (e) {
        return [];
      }
    });
  }

  public getContentUrl(): string {
    const contentId = parseInt(this.variableMap.get(this.fields.annotatedDocument));
    return this.activitiContentService.contentApi.getRawContentUrl(contentId);
  }

  public downloadContent($event: MouseEvent) {
    $event.preventDefault();
    const annotatedDocumentId: string = this.variableMap.get(this.fields.annotatedDocument);
    try {
      const annotatedDocumentFilename: unknown = this.variableMap.get(this.fields.annotatedDocumentFilename);
      fromPromise(this.activitiContentService.contentApi.getRawContent(parseInt(annotatedDocumentId))).subscribe(
        (content) => {
          saveAs(content, annotatedDocumentFilename);
        }
      );
    } catch (e) {
      console.error(e);
    }

  }

  public hasWorkflowProtocolByResult(workflowProtocol: WorkflowResultProtocol[], result: string): boolean {
    return !!workflowProtocol.find((entry) => entry.result === result);
  }

  public filterWorkflowProtocolByResult(workflowProtocol: WorkflowResultProtocol[], result: string): WorkflowResultProtocol[] {
    return workflowProtocol.filter((entry) => entry.result === result);
  }
}
